import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import DashboardGadgetsContext from '../../contexts/DashboardGadgetsContext';
import ToastContext from '../../contexts/ToastContext';
import MetricListsContext from '../../contexts/MetricListsContext';
import usePopup from '../../hooks/usePopup';
import captureException from '../../services/captureException';
import ConfigureDashboardGadget from '../ConfigureDashboardGadget';
import ConfigureMetricList from '../MetricListGadget/ConfigureMetricList';
import useOnDashboardGadgetEditSaved from './useOnDashboardGadgetEditSaved';
import DateInputContext from '../../contexts/DateInputContext';

type EditingGadgetType =
  | { type: 'metricList'; metricList: MetricListGadgetType }
  | { type: 'dashboardGadget'; dashboardGadget: DashboardGadget }
  | undefined;

const GadgetSettingsLinkHandler = () => {
  const { showToast } = useContext(ToastContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);
  const { metricListsLookup } = useContext(MetricListsContext);
  const originalListContext = useContext(DateInputContext);

  const { isOpen, open, close } = usePopup();
  const [editingGadget, setEditingGadget] = useState<EditingGadgetType>();
  const onDashboardGadgetEditSaved = useOnDashboardGadgetEditSaved();

  // Remove the query param once the user dismisses modal
  const onFormClosed = useCallback(() => {
    setSearchParams('');
    close();
  }, [close, setSearchParams]);

  // Find and open the settings of a gadget
  useEffect(() => {
    const gadgetSearch = searchParams.get('gadgetId');
    if (!gadgetSearch) return;

    const dashboardGadget = dashboardGadgetsLookup[gadgetSearch];
    if (dashboardGadget) {
      setEditingGadget({
        type: 'dashboardGadget',
        dashboardGadget: dashboardGadget,
      });
      open();
      return;
    }

    const metricList = metricListsLookup[gadgetSearch];
    if (metricList) {
      setEditingGadget({ type: 'metricList', metricList });
      open();
      return;
    }

    const e = new Error();
    e.name = 'GadgetSettingsLinkHandler: gadget not found';
    captureException(e);
    setSearchParams('');
    showToast('Gadget not found');
  }, [
    dashboardGadgetsLookup,
    metricListsLookup,
    open,
    searchParams,
    setSearchParams,
    showToast,
  ]);

  const SettingsComponent = useMemo(() => {
    if (!editingGadget) return null;

    if (editingGadget.type === 'dashboardGadget') {
      return (
        <ConfigureDashboardGadget
          editingGadget={editingGadget.dashboardGadget}
          close={onFormClosed}
          onEditSave={onDashboardGadgetEditSaved}
        />
      );
    }

    if (editingGadget.type === 'metricList') {
      return (
        <ConfigureMetricList
          metricListGadget={editingGadget.metricList}
          close={onFormClosed}
          originalListContext={originalListContext}
        />
      );
    }
  }, [
    editingGadget,
    onDashboardGadgetEditSaved,
    onFormClosed,
    originalListContext,
  ]);

  return (
    <ModalTransition>
      {isOpen && (
        <Modal
          width={'95vw'}
          height={'80vh'}
          shouldScrollInViewport={false}
          autoFocus={false}
          onClose={onFormClosed}
        >
          {SettingsComponent}
        </Modal>
      )}
    </ModalTransition>
  );
};

export default GadgetSettingsLinkHandler;
